// library imports
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PageContent, PageHeader, PageWrapper, PageContainer } from 'components/common/layout_element/LayoutElement';
import * as Constants from "_core/configs/Constants";

// User Component imports
import { Row, Button, TextInput, Stack, Card, Icon, Uploader, Text, CommonModal, FlexibleTextInput } from "_core/components/common/UI";
import { setLoading, setMenuState, setErrorModal, setInitializeState } from "_core/slices/scene/SceneSlice";

// API imports
import AdminHrNewsApi from "apis/AdminHrNewsApi";
import AdminHrNewsSaveApi from "apis/AdminHrNewsSaveApi";
import AdminHrNewsDeleteApi from "apis/AdminHrNewsDeleteApi";
import AdminHrNewsUploadApi from "apis/AdminHrNewsUploadApi";
import AdminHrNewsEndpageSaveApi from "apis/AdminHrNewsEndpageSaveApi";

// styles imports
import './AdminHrNewsEdit.scss';


// ─── 日時変換ヘルパ ───────────────────────────────

// APIレスポンスのISO文字列 → Date オブジェクト
const parseApiDateTime = (isoStr) => {
    if (!isoStr) return null;
    const d = new Date(isoStr);
    return isNaN(d.getTime()) ? null : d;
};

// Date オブジェクト → API送信用ISO文字列（YYYY-MM-DDTHH:MM:SS、ローカルタイム）
const formatApiDateTime = (date) => {
    if (!date) return "";
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

// 日時を「開始用」に整える: 分=0、秒=0 へ。HH:00:00（時間単位の刻み）
const normalizeStartDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    d.setMinutes(0);
    d.setSeconds(0);
    d.setMilliseconds(0);
    return d;
};

// 日時を「終了用」に整える: 分=59、秒=59 へ。HH:59:59（その時間の終わりまで）
const normalizeEndDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    d.setMinutes(59);
    d.setSeconds(59);
    d.setMilliseconds(0);
    return d;
};


// ─── ステータス算出ヘルパ ───────────────────────────────

// ステータス定義（フィルタタブ/チップ/タイムラインで共通利用）
const STATUS = {
    ACTIVE: "active",   // 公開中
    UPCOMING: "upcoming", // 公開予定
    EXPIRED: "expired", // 公開終了
};

const STATUS_LABEL = {
    [STATUS.ACTIVE]: "公開中",
    [STATUS.UPCOMING]: "公開予定",
    [STATUS.EXPIRED]: "公開終了",
};

// 現在日時を基準に各アイテムのステータスを判定
const computeStatus = (item, now) => {
    const start = parseApiDateTime(item.start_date);
    const end = parseApiDateTime(item.end_date);
    if (!start || !end) return STATUS.EXPIRED;
    if (now < start) return STATUS.UPCOMING;
    if (now > end) return STATUS.EXPIRED;
    return STATUS.ACTIVE;
};

// 残り日数/開始までの日数を「あと N 日」「N 日後に開始」表記で返す
const formatRemainingHint = (item, now) => {
    const start = parseApiDateTime(item.start_date);
    const end = parseApiDateTime(item.end_date);
    if (!start || !end) return "";
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const status = computeStatus(item, now);
    if (status === STATUS.ACTIVE) {
        const diff = Math.ceil((end.getTime() - now.getTime()) / MS_PER_DAY);
        if (diff <= 0) return "本日終了";
        return `あと ${diff} 日`;
    }
    if (status === STATUS.UPCOMING) {
        const diff = Math.ceil((start.getTime() - now.getTime()) / MS_PER_DAY);
        if (diff <= 0) return "まもなく開始";
        return `${diff} 日後に開始`;
    }
    return "";
};

// 一覧の期間表示用フォーマッタ（"2026/05/21 12:34" を返す、秒は省略）
const formatPeriodShort = (isoStr) => {
    const d = parseApiDateTime(isoStr);
    if (!d) return "";
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ` +
        `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};


// バナーアップロードで受け付ける拡張子（画像 + 動画）。Uploader.accept とラベル表示で共用する
const ACCEPTED_BANNER_EXTS = [".png", ".jpg", ".jpeg", ".webp", ".mp4", ".webm"];

// エンドページ機能ショートカットボタンの最大登録数（バックエンドの constants.HR_NEWS_ENDPAGE_BUTTON_MAX と揃える）
const ENDPAGE_BUTTON_MAX = 6;

// URL / ファイル名から動画かどうかを判定（拡張子ベース）
const isVideoPath = (path) => {
    if (!path) return false;
    // クエリパラメータ（SAS URL に付く）は除去してから拡張子判定
    const cleanPath = path.split("?")[0].split("#")[0];
    return /\.(mp4|webm)$/i.test(cleanPath);
};


const buildEmptyForm = () => ({
    id: 0,
    banner_path: "",          // DB保存用の生Blobパス（hr_news_upload で得る）
    banner_preview_url: "",   // 表示用 SAS URL（プレビューだけに使用）
    card_text: "",
    url: "",
    description: "",
    start_date: null,         // Date オブジェクト（react-datepicker用）
    end_date: null,
});


const AdminHrNewsEdit = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ExecAdminHrNewsApi = AdminHrNewsApi();
    const ExecAdminHrNewsSaveApi = AdminHrNewsSaveApi();
    const ExecAdminHrNewsDeleteApi = AdminHrNewsDeleteApi();
    const ExecAdminHrNewsUploadApi = AdminHrNewsUploadApi();
    const ExecAdminHrNewsEndpageSaveApi = AdminHrNewsEndpageSaveApi();

    const { initializeState } = useSelector((store) => store.scene);

    // スライド一覧
    const [newsList, setNewsList] = useState([]);
    // 編集中のフォーム（null なら一覧表示モード、オブジェクトなら編集モード）
    const [editingForm, setEditingForm] = useState(null);
    // バナーアップローダ用のファイル配列
    const [bannerFiles, setBannerFiles] = useState([]);
    // 削除確認モーダル
    const [modalInfo, setModalInfo] = useState({ show: false });

    // エンドページ設定モード（true なら機能ショートカットボタン編集画面）と、その編集中ボタン配列
    const [endpageMode, setEndpageMode] = useState(false);
    const [endpageButtons, setEndpageButtons] = useState([]); // [{ label, message, target_id, arrow_position }]
    // 読み込み直後のスナップショット（差分検出＝保存ボタン活性判定に使用）
    const [initialEndpageButtons, setInitialEndpageButtons] = useState([]);

    // フィルタタブ（"all" | STATUS.ACTIVE | STATUS.UPCOMING | STATUS.EXPIRED）
    const [filter, setFilter] = useState("all");

    // タイムラインの表示レンジ切替（"month" = 後30日 / "week" = 後7日。前は常に 7日）
    const [timelineMode, setTimelineMode] = useState("month");

    // タイムライン/ステータス計算の基準となる「現在時刻」。1分ごとに更新して残り日数を最新化。
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 60 * 1000);
        return () => clearInterval(id);
    }, []);

    // 日時ピッカー（"start" or "end" or null）。CustomDateRangePicker と同じく click→dialog で inline カレンダーを表示する。
    const [openPicker, setOpenPicker] = useState(null);
    const datetimeDialogRef = useRef(null);

    // 日時テキスト入力欄のローカル state（任意の文字列を一時保持し、blur 時に確定する）
    const [startInputText, setStartInputText] = useState("");
    const [endInputText, setEndInputText] = useState("");

    // ダイアログ外クリックでカレンダーを閉じる。トリガー欄（input + カレンダーボタン）の中をクリックした場合は閉じない。
    useEffect(() => {
        if (!openPicker) return;
        const onClickOutside = (e) => {
            if (datetimeDialogRef.current && datetimeDialogRef.current.contains(e.target)) return;
            if (e.target.closest && e.target.closest(".hr-news-datetime-trigger-wrap")) return;
            setOpenPicker(null);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, [openPicker]);

    useEffect(() => {
        dispatch(setMenuState({ isHeader: false, isMenu: true, isFooter: false, isFullScreen: false }));
    }, [dispatch]);

    useEffect(() => {
        if (initializeState !== Constants.INITIALIZE_STATE.allComplete) {
            return;
        }
        fetchList();
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete));
    }, [initializeState]);

    // サブ画面（ニュース編集 or エンドページ設定）中はブラウザ履歴に1エントリ追加し、戻るボタンで一覧に戻れるようにする
    const isInSubScreen = editingForm !== null || endpageMode;
    useEffect(() => {
        if (!isInSubScreen) return;
        window.history.pushState({ hrNewsSubScreen: true }, "");
        const handlePopState = () => {
            setEditingForm(null);
            setBannerFiles([]);
            setEndpageMode(false);
        };
        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, [isInSubScreen]);

    // ───── 入力バリデーション ─────

    // 保存可能か判定。必須はバナー・概要・開始/終了日時・開始≦終了。
    // ひとこと（card_text）と 遷移先URL は任意（未入力でも保存可）。
    const canSave = () => {
        if (!editingForm) return false;
        // 編集時は banner_path が API レスポンスに含まれないケースに備え、banner_preview_url（SAS URL）も既存バナーの存在判定に使う。
        const hasBanner = (editingForm.banner_path && editingForm.banner_path.length > 0)
            || (editingForm.banner_preview_url && editingForm.banner_preview_url.length > 0)
            || (bannerFiles && bannerFiles.length > 0);
        if (!hasBanner) return false;
        // if (!(editingForm.description || "").trim()) return false;
        if (!editingForm.start_date || !editingForm.end_date) return false;
        // 開始 ≦ 終了（補正後で比較）
        const s = normalizeStartDate(editingForm.start_date);
        const e = normalizeEndDate(editingForm.end_date);
        if (!s || !e || s.getTime() > e.getTime()) return false;
        return true;
    };

    // ───── API 呼び出し ─────

    // 一覧取得
    const fetchList = async () => {
        dispatch(setLoading(true));
        const result = await ExecAdminHrNewsApi();
        dispatch(setLoading(false));

        if (result.error || (result.response && result.response.error_code > 0)) {
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            return;
        }
        const list = result.response.t_hr_news_list || [];
        // kind=1: ニューススライド（一覧表示）、kind=2: エンドページ設定（専用1行、description に JSON を保持）
        setNewsList(list.filter(item => item.kind !== 2));
        const endpageRow = list.find(item => item.kind === 2);
        let buttons = [];
        if (endpageRow && endpageRow.description) {
            try {
                const parsed = JSON.parse(endpageRow.description);
                if (parsed && Array.isArray(parsed.buttons)) buttons = parsed.buttons;
            } catch (e) {
                buttons = [];
            }
        }
        const normalized = buttons.map(b => ({ label: b.label || "", message: b.message || "", target_id: b.target_id || "", arrow_position: b.arrow_position || "t" }));
        setEndpageButtons(normalized);
        // 差分検出用のスナップショット（読み込み直後＝未変更）
        setInitialEndpageButtons(normalized);
    };

    // 保存（新規 or 更新）。バナー画像が選択されていれば先にアップロードしてから保存APIを叩く。
    const saveNews = async () => {
        if (!editingForm) return;

        // 簡易バリデーション
        if (!editingForm.start_date || !editingForm.end_date) {
            dispatch(setErrorModal({ show: true, text: {
                code: "入力エラー",
                message: "表示開始日時と終了日時を入力してください。"
            }}));
            return;
        }
        // 開始=HH:mm:00、終了=HH:59:59 に補正してから開始≦終了をチェック
        const startDate = normalizeStartDate(editingForm.start_date);
        const endDate = normalizeEndDate(editingForm.end_date);
        if (startDate.getTime() > endDate.getTime()) {
            dispatch(setErrorModal({ show: true, text: {
                code: "入力エラー",
                message: "表示開始日時は終了日時より前である必要があります。"
            }}));
            return;
        }

        dispatch(setLoading(true));

        // ① バナー画像が選択されていればここでアップロード（保存ボタン押下時に初めてサーバ送信）
        let bannerPath = editingForm.banner_path;
        if (bannerFiles && bannerFiles.length > 0) {
            const fileObj = bannerFiles[0].file || bannerFiles[0];
            const filename = bannerFiles[0].filename || (fileObj && fileObj.name) || "banner";
            const uploadResult = await ExecAdminHrNewsUploadApi("banner", fileObj, filename);
            if (uploadResult.error || (uploadResult.response && uploadResult.response.error_code > 0)) {
                dispatch(setLoading(false));
                dispatch(setErrorModal({ show: true, text: uploadResult.response.error_detail }));
                return;
            }
            bannerPath = uploadResult.response.path;
        }

        // ② 保存API
        const result = await ExecAdminHrNewsSaveApi({
            ...editingForm,
            banner_path: bannerPath,
            start_date: formatApiDateTime(startDate),
            end_date: formatApiDateTime(endDate),
        });
        dispatch(setLoading(false));

        if (result.error || (result.response && result.response.error_code > 0)) {
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            return;
        }
        // 編集モードを抜けて一覧再取得
        setBannerFiles([]);
        exitEditMode();
        await fetchList();
    };

    // 論理削除
    const deleteNews = async (id) => {
        dispatch(setLoading(true));
        const result = await ExecAdminHrNewsDeleteApi(id);
        dispatch(setLoading(false));

        if (result.error || (result.response && result.response.error_code > 0)) {
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            return;
        }
        await fetchList();
    };

    // バナーファイル選択時：Uploader が自前でプレビューを出すので、ここでは bannerFiles を更新するだけ。
    // editingForm.banner_preview_url は startEdit で API の SAS URL が入っており、編集時の「既存ファイル表示」として保持する。
    const onBannerSelected = (files) => {
        setBannerFiles(files);
    };

    // ───── UI ハンドラ ─────

    // サブ画面を抜ける（pushState した履歴を戻して popstate ハンドラに任せる）
    const exitEditMode = () => {
        if (window.history.state && window.history.state.hrNewsSubScreen) {
            window.history.back();
        } else {
            setEditingForm(null);
            setBannerFiles([]);
            setEndpageMode(false);
        }
    };

    // ───── エンドページ設定 ─────

    // エンドページ設定画面を開く。保存済みスナップショットから編集状態を作り直す（前回の未保存編集は破棄）。
    const startEndpageSettings = () => {
        setEndpageButtons(initialEndpageButtons.map(b => ({ label: b.label || "", message: b.message || "", target_id: b.target_id || "", arrow_position: b.arrow_position || "t" })));
        setEndpageMode(true);
    };

    const addEndpageButton = () => {
        if (endpageButtons.length >= ENDPAGE_BUTTON_MAX) return;
        setEndpageButtons(prev => [...prev, { label: "", message: "", target_id: "", arrow_position: "t" }]);
    };

    const removeEndpageButton = (index) => {
        setEndpageButtons(prev => prev.filter((_, i) => i !== index));
    };

    const updateEndpageButton = (index, key, value) => {
        setEndpageButtons(prev => prev.map((b, i) => (i === index ? { ...b, [key]: value } : b)));
    };

    // 保存可能か: 読み込み時から変更があり（API実行の必要がある）、かつ全ボタンの label/message が入力済みのときのみ。
    const canSaveEndpage = () => {
        // 全ボタンが入力済みか
        const allFilled = endpageButtons.every(b => (b.label || "").trim() && (b.message || "").trim());
        if (!allFilled) return false;
        // 読み込み時のスナップショットと比較して差分があるか
        const norm = (arr) => JSON.stringify(arr.map(b => ({ label: (b.label || "").trim(), message: (b.message || "").trim(), target_id: (b.target_id || "").trim(), arrow_position: (b.arrow_position || "t") })));
        return norm(endpageButtons) !== norm(initialEndpageButtons);
    };

    const saveEndpage = async () => {
        if (!canSaveEndpage()) return;
        const buttons = endpageButtons.map(b => ({ label: (b.label || "").trim(), message: (b.message || "").trim(), target_id: (b.target_id || "").trim(), arrow_position: (b.arrow_position || "t") }));
        dispatch(setLoading(true));
        const result = await ExecAdminHrNewsEndpageSaveApi({ buttons });
        dispatch(setLoading(false));
        if (result.error || (result.response && result.response.error_code > 0)) {
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            return;
        }
        exitEditMode();
        await fetchList();
    };

    const startCreate = () => {
        setEditingForm(buildEmptyForm());
        setBannerFiles([]);
    };

    const startEdit = (item) => {
        // ピッカーの時刻リストは :00 区切りで描画されるため、開始/終了とも内部状態は HH:00:00 に丸めて保持する。
        // 表示と保存時に開始は :00:00、終了は :59:59 を当て直す。
        const snapToHour = (d) => {
            if (!d) return null;
            const ret = new Date(d);
            ret.setMinutes(0);
            ret.setSeconds(0);
            ret.setMilliseconds(0);
            return ret;
        };
        setEditingForm({
            id: item.id,
            banner_path: item.banner_path || "",       // DB保存用の生パス
            banner_preview_url: item.banner_url || "", // 表示用 SAS URL
            card_text: item.card_text || "",
            url: item.url || "",
            description: item.description || "",
            start_date: snapToHour(parseApiDateTime(item.start_date)),
            end_date: snapToHour(parseApiDateTime(item.end_date)),
        });
        setBannerFiles([]);
    };

    const cancelEdit = () => {
        exitEditMode();
    };

    const updateForm = (key, value) => {
        setEditingForm(prev => ({ ...prev, [key]: value }));
    };

    const confirmDelete = (item) => {
        const itemId = item.id;
        const itemLabel = item.card_text || "（テキスト未設定）";
        setModalInfo({
            show: true,
            title: "削除確認",
            text: `削除したファイルは元に戻すことはできません。</br>削除してもよろしいですか？`,
            confirmvalue: "削除",
            cancelvalue: "キャンセル",
            onclick: () => {
                // CommonModal 側で閉じてくれるので、ここでは削除APIだけ叩く
                deleteNews(itemId);
            },
        });
    };

    // ───── 日時ピッカー（CustomDateRangePicker と同じ click→dialog の挙動 ＋ テキスト直接入力併用） ─────

    // フォームの値を表示用文字列にフォーマット
    // 開始: HH:00:00（時間単位）
    // 終了: HH:59:59（その時間の終わりまで）
    // どちらも内部状態は HH:00 を保持してピッカーの時刻リスト選択を正しくハイライトさせる
    const formatTriggerValue = (date, kind) => {
        if (!date) return "";
        const pad = (n) => String(n).padStart(2, '0');
        const tail = kind === "end" ? "59:59" : "00:00";
        return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ` +
            `${pad(date.getHours())}:${tail}`;
    };

    // テキスト入力をパース。"yyyy/MM/dd HH:mm" or "yyyy/MM/dd HH:mm:ss"（区切り '/' '-' ' ' 'T' を許容）
    const parseDateTimeText = (str) => {
        if (!str) return null;
        const m = str.trim().match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})[\sT](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/);
        if (!m) return null;
        const [, y, mo, d, h, mi, s] = m;
        const date = new Date(+y, +mo - 1, +d, +h, +mi, s ? +s : 0);
        if (isNaN(date.getTime())) return null;
        // 2/31 → 3/3 のような暦補正を検知して拒否
        if (date.getFullYear() !== +y || date.getMonth() !== +mo - 1 || date.getDate() !== +d) return null;
        if (+h > 23 || +mi > 59) return null;
        return date;
    };

    // editingForm の日時が変わったら入力欄の表示文字列を再同期（カレンダーで選択した時など外部更新を反映）
    useEffect(() => {
        if (!editingForm) {
            setStartInputText("");
            setEndInputText("");
            return;
        }
        setStartInputText(editingForm.start_date ? formatTriggerValue(editingForm.start_date, "start") : "");
        setEndInputText(editingForm.end_date ? formatTriggerValue(editingForm.end_date, "end") : "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingForm && editingForm.start_date, editingForm && editingForm.end_date]);

    // 入力欄 blur 時にテキストをパースして editingForm へ確定。失敗時は表示を元に戻す。
    const commitTriggerInput = (kind) => {
        const inputText = kind === "start" ? startInputText : endInputText;
        const currentDate = kind === "start" ? editingForm.start_date : editingForm.end_date;
        if (!inputText.trim()) {
            // 空入力は値クリア
            updateForm(kind === "start" ? "start_date" : "end_date", null);
            return;
        }
        const parsed = parseDateTimeText(inputText);
        if (!parsed) {
            // パース失敗 → 元の表示に戻す
            const restoreFn = kind === "start" ? setStartInputText : setEndInputText;
            restoreFn(currentDate ? formatTriggerValue(currentDate, kind) : "");
            return;
        }
        // 開始/終了どちらも HH:00 に丸めて格納（ピッカーの時刻リスト選択ハイライトを維持するため）。
        // 表示は formatTriggerValue が ":00:00" or ":59:59" に整える。
        const normalized = new Date(parsed);
        normalized.setMinutes(0);
        normalized.setSeconds(0);
        normalized.setMilliseconds(0);
        updateForm(kind === "start" ? "start_date" : "end_date", normalized);
    };

    // 入力欄 + カレンダーアイコンの組み合わせ。テキスト直接入力もカレンダー UI も両方使える。
    const renderDateTimeTrigger = (kind) => {
        const placeholder = kind === "start"
            ? "yyyy/MM/dd HH:00:00"
            : "yyyy/MM/dd HH:59:59";
        const inputText = kind === "start" ? startInputText : endInputText;
        const setInputText = kind === "start" ? setStartInputText : setEndInputText;
        return (
            <div className="hr-news-datetime-trigger-wrap">
                <input
                    type="text"
                    className="hr-news-datetime-trigger-input"
                    value={inputText}
                    placeholder={placeholder}
                    onChange={(e) => setInputText(e.target.value)}
                    onBlur={() => commitTriggerInput(kind)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                />
                <button
                    type="button"
                    className="hr-news-datetime-calendar-button"
                    onClick={() => setOpenPicker(kind)}
                    aria-label="カレンダーを開く"
                >
                    <Icon icon="calendar" size={16} />
                </button>
            </div>
        );
    };

    // 固定ポジションのダイアログ。中身は CustomDateRangePicker と同じ Card 構造を踏襲し、SCSS の `.date-range-picker` を継承して見た目を完全に揃える。
    const renderDateTimeDialog = () => {
        const kind = openPicker;
        const value = kind === "start" ? editingForm.start_date : editingForm.end_date;
        // picker の出力をそのまま保持する（ピッカー側の選択ハイライト維持のため正規化しない）。
        // 整形は formatTriggerValue で表示時、normalizeStartDate/EndDate で保存時に行う。
        const onChange = (d) => {
            if (!d) return;
            updateForm(kind === "start" ? "start_date" : "end_date", d);
        };
        return (
            <div className="hr-news-datetime-dialog" ref={datetimeDialogRef}>
                <Card className="date-range-picker solid-card">
                    <Stack className="spacing-8px">
                        <div className="date-picker-header">
                            <Stack className="align-center">
                                <span>{value ? formatTriggerValue(value, kind) : (kind === "start" ? "開始日時を選択" : "終了日時を選択")}</span>
                            </Stack>
                        </div>
                        <hr />
                        <Row className="flex-center">
                            <DatePicker
                                selected={value}
                                onChange={onChange}
                                showTimeSelect
                                timeIntervals={60}
                                timeFormat={kind === "start" ? "HH:00" : "HH:59"}
                                timeCaption="時"
                                inline
                            />
                        </Row>
                        <Row className="flex-center spacing-8px">
                            <Button className="primary width-160px" onClick={() => setOpenPicker(null)}>確定</Button>
                        </Row>
                    </Stack>
                </Card>
            </div>
        );
    };

    // ───── レンダリング ─────

    // タイムライン描画範囲: 前は常に 7 日、後は表示モードで切替（30日 / 7日）
    const TIMELINE_DAYS_BEFORE = 7;
    const TIMELINE_DAYS_AFTER = timelineMode === "week" ? 7 : 30;
    const TIMELINE_TOTAL_DAYS = TIMELINE_DAYS_BEFORE + TIMELINE_DAYS_AFTER;

    // フィルタタブで絞り込まれた一覧。"all" 以外のときはドラッグ並び替えを抑止（インデックスが全体と一致しないため）。
    const filteredList = filter === "all"
        ? newsList
        : newsList.filter((item) => computeStatus(item, now) === filter);

    // 各ステータスの件数（タブの後ろに表示）
    const counts = {
        all: newsList.length,
        [STATUS.ACTIVE]: 0,
        [STATUS.UPCOMING]: 0,
        [STATUS.EXPIRED]: 0,
    };
    newsList.forEach((item) => { counts[computeStatus(item, now)]++; });

    // ガントタイムラインの 1 行を、開始/終了日に応じて左端からの % で帯描画
    const renderTimelineBar = (item) => {
        const start = parseApiDateTime(item.start_date);
        const end = parseApiDateTime(item.end_date);
        if (!start || !end) return null;
        const MS_PER_DAY = 24 * 60 * 60 * 1000;
        // 軸の日次グリッドと整列させるため、viewStart は「今日 00:00 - 前日数」を基準にする
        const todayMidnight = new Date(now);
        todayMidnight.setHours(0, 0, 0, 0);
        const viewStart = new Date(todayMidnight.getTime() - TIMELINE_DAYS_BEFORE * MS_PER_DAY);
        const viewEnd = new Date(viewStart.getTime() + TIMELINE_TOTAL_DAYS * MS_PER_DAY);
        // ビューポート外完全外なら描画しない
        if (end < viewStart || start > viewEnd) return null;
        const clamp = (d) => new Date(Math.max(viewStart.getTime(), Math.min(viewEnd.getTime(), d.getTime())));
        const left = ((clamp(start).getTime() - viewStart.getTime()) / (TIMELINE_TOTAL_DAYS * MS_PER_DAY)) * 100;
        const right = ((clamp(end).getTime() - viewStart.getTime()) / (TIMELINE_TOTAL_DAYS * MS_PER_DAY)) * 100;
        const width = Math.max(right - left, 1);
        const status = computeStatus(item, now);
        return (
            <div
                className={`hr-news-timeline-bar status-${status}`}
                style={{ left: `${left}%`, width: `${width}%` }}
                title={`${item.card_text || "(テキスト未設定)"}\n${formatPeriodShort(item.start_date)} ～ ${formatPeriodShort(item.end_date)}`}
            >
                <span className="hr-news-timeline-bar-label">{item.card_text || "(無題)"}</span>
            </div>
        );
    };

    const renderTimeline = () => {
        if (newsList.length === 0) return null;
        const MS_PER_DAY = 24 * 60 * 60 * 1000;
        // ビュー範囲。今日 00:00 を基準にすると日次グリッドが整数日に整列する
        const todayMidnight = new Date(now);
        todayMidnight.setHours(0, 0, 0, 0);
        const viewStart = new Date(todayMidnight.getTime() - TIMELINE_DAYS_BEFORE * MS_PER_DAY);
        // 「今日」の縦線位置: 現在時刻を viewStart からの相対比で配置
        const todayPct = ((now.getTime() - viewStart.getTime()) / (TIMELINE_TOTAL_DAYS * MS_PER_DAY)) * 100;

        // 日次マイナーグリッド（C案: ガント感のための薄い縦線）。両端と「今日」位置は別レイヤで描画するので除外。
        const dayLines = [];
        for (let d = 1; d < TIMELINE_TOTAL_DAYS; d++) {
            dayLines.push({ pct: (d / TIMELINE_TOTAL_DAYS) * 100 });
        }

        // 週次ラベル（暦の月曜基準、毎週月曜にラベルを置く）と月境界マーカー（1日に太線＋「N月」）
        const weekLabels = [];
        const monthMarkers = [];
        for (let d = 0; d <= TIMELINE_TOTAL_DAYS; d++) {
            const date = new Date(viewStart.getTime() + d * MS_PER_DAY);
            const pct = (d / TIMELINE_TOTAL_DAYS) * 100;
            // 月境界（1日）: 月ラベル + 太線。これは週ラベルと位置が被っても優先表示。
            if (date.getDate() === 1) {
                monthMarkers.push({ pct, label: `${date.getMonth() + 1}月` });
            }
            // 月曜ラベル。ただし「今日」とほぼ同位置 or 月境界と同位置 or 範囲の両端付近のときは省略
            const isEdge = d === 0 || d === TIMELINE_TOTAL_DAYS;
            if (date.getDay() === 1 && Math.abs(pct - todayPct) > 3 && date.getDate() !== 1 && !isEdge) {
                weekLabels.push({ pct, label: `${date.getMonth() + 1}/${date.getDate()}` });
            }
        }

        // 始点日 / 終点日のラベル（範囲の両端を明示）
        const viewEndDate = new Date(viewStart.getTime() + TIMELINE_TOTAL_DAYS * MS_PER_DAY);
        const formatEdge = (d) => `${String(d.getMonth() + 1)}/${String(d.getDate())}`;

        return (
            <Card className="base-card hr-news-timeline-card">
                <Stack className="spacing-8px">
                    <Row className="flex-both align-center">
                        <div className="text-bold text-size-normal">公開スケジュール</div>
                        <Row className="hr-news-timeline-modes spacing-4px">
                            <button
                                type="button"
                                className={"hr-news-timeline-mode" + (timelineMode === "month" ? " active" : "")}
                                onClick={() => setTimelineMode("month")}
                            >
                                30日
                            </button>
                            <button
                                type="button"
                                className={"hr-news-timeline-mode" + (timelineMode === "week" ? " active" : "")}
                                onClick={() => setTimelineMode("week")}
                            >
                                7日
                            </button>
                        </Row>
                    </Row>
                    <div className="hr-news-timeline-axis">
                        {/* 日次の薄い縦線（軸の高さ全体を貫通） */}
                        {dayLines.map((l, i) => (
                            <div key={`d${i}`} className="hr-news-timeline-day-line" style={{ left: `${l.pct}%` }} />
                        ))}
                        {/* 週次ラベル（月曜） */}
                        {weekLabels.map((t, i) => (
                            <div key={`w${i}`} className="hr-news-timeline-tick" style={{ left: `${t.pct}%` }}>
                                <span className="hr-news-timeline-tick-label">{t.label}</span>
                            </div>
                        ))}
                        {/* 月境界（1日）: 太い縦線＋「N月」ラベル */}
                        {monthMarkers.map((m, i) => (
                            <div key={`m${i}`} className="hr-news-timeline-month-marker" style={{ left: `${m.pct}%` }}>
                                <span className="hr-news-timeline-month-label">{m.label}</span>
                            </div>
                        ))}
                        {/* 範囲の始点日（左端）と終点日（右端）: 範囲が一目で分かるよう常時表示 */}
                        <div className="hr-news-timeline-edge start" style={{ left: 0 }}>
                            <span className="hr-news-timeline-edge-label">{formatEdge(viewStart)}</span>
                        </div>
                        <div className="hr-news-timeline-edge end" style={{ left: "100%" }}>
                            <span className="hr-news-timeline-edge-label">{formatEdge(viewEndDate)}</span>
                        </div>
                        <div className="hr-news-timeline-today" style={{ left: `${todayPct}%` }}>
                            <span className="hr-news-timeline-today-label">今日</span>
                        </div>
                    </div>
                    <div className="hr-news-timeline-body">
                        {newsList.map((item) => (
                            <div key={item.id} className="hr-news-timeline-track">
                                {/* 日次グリッドを行内にも薄く敷く（バーの背景としてガント感を出す） */}
                                {dayLines.map((l, i) => (
                                    <div key={`db${i}`} className="hr-news-timeline-day-line-bg" style={{ left: `${l.pct}%` }} />
                                ))}
                                {monthMarkers.map((m, i) => (
                                    <div key={`mb${i}`} className="hr-news-timeline-month-line-bg" style={{ left: `${m.pct}%` }} />
                                ))}
                                <div className="hr-news-timeline-today-line" style={{ left: `${todayPct}%` }} />
                                {renderTimelineBar(item)}
                            </div>
                        ))}
                    </div>
                </Stack>
            </Card>
        );
    };

    const renderFilterTabs = () => {
        const tabs = [
            { key: "all", label: "すべて" },
            { key: STATUS.ACTIVE, label: STATUS_LABEL[STATUS.ACTIVE] },
            { key: STATUS.UPCOMING, label: STATUS_LABEL[STATUS.UPCOMING] },
            { key: STATUS.EXPIRED, label: STATUS_LABEL[STATUS.EXPIRED] },
        ];
        return (
            <Row className="hr-news-filter-tabs spacing-8px align-center">
                {tabs.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        className={"hr-news-filter-tab" + (filter === t.key ? " active" : "")}
                        onClick={() => setFilter(t.key)}
                    >
                        <span className="hr-news-filter-tab-label">{t.label}</span>
                        <span className="hr-news-filter-tab-count">{counts[t.key]}</span>
                    </button>
                ))}
            </Row>
        );
    };

    const renderListRow = (item) => {
        const status = computeStatus(item, now);
        // 動画は <video preload="metadata" muted> で先頭フレームをサムネ代わりに表示
        const renderThumb = () => {
            if (!item.banner_url) return (
                <div className="hr-news-thumb-empty">
                    <Text className="text-color-sub text-size-small">(画像未設定)</Text>
                </div>
            );
            if (isVideoPath(item.banner_path || item.banner_url)) {
                return <video src={item.banner_url} className="hr-news-thumb-video" muted playsInline preload="metadata" />;
            }
            return <img src={item.banner_url} alt="banner" className="hr-news-thumb" />;
        };
        return (
            <div key={item.id} className="hr-news-row">
                {/* 列1: サムネイル（grid 自動幅） */}
                {renderThumb()}
                {/* 列2: 本文（grid 中央列 minmax(0, 1fr) で必ず縮小可能） */}
                <Stack className="spacing-2px hr-news-row-body">
                    <Row className="spacing-8px align-center">
                        <span className={`hr-news-status-chip status-${status}`}>{STATUS_LABEL[status]}</span>
                        <span className="hr-news-remaining-hint text-size-small text-color-sub">{formatRemainingHint(item, now)}</span>
                        <span className="hr-news-card-text text-size-normal" title={item.card_text || ""}>{item.card_text || "（テキスト未設定）"}</span>
                    </Row>
                    <div className="text-size-small text-color-sub">
                        {formatPeriodShort(item.start_date)} ～ {formatPeriodShort(item.end_date)}
                    </div>
                    {item.url && (
                        <div className="text-size-small text-color-link text-truncate">{item.url}</div>
                    )}
                </Stack>
                {/* 列3: アクション（flex-shrink: 0 で常時表示確保） */}
                <div className="hr-news-row-actions">
                    <Button className="operate-button secondary-border primary" onClick={() => startEdit(item)}>
                        <Row className="text-size-normal text-normal-weight operate-label">編集</Row>
                    </Button>
                    <Button className="operate-button secondary-border danger" onClick={() => confirmDelete(item)}>
                        <Row className="text-size-normal text-normal-weight operate-label">削除</Row>
                    </Button>
                </div>
            </div>
        );
    };

    const renderList = () => (
        <Stack className="spacing-16px">
            {/* {renderTimeline()} */}
            {renderFilterTabs()}
            <Stack className="spacing-8px">
                {filteredList.length === 0 && (
                    <Card className="base-card">
                        <Stack className="align-center spacing-8px padding-32px">
                            <Text className="text-color-sub">
                                {newsList.length === 0
                                    ? "登録されているHRニュースがありません"
                                    : "該当するHRニュースがありません"}
                            </Text>
                        </Stack>
                    </Card>
                )}
                {filteredList.map((item) => renderListRow(item))}
            </Stack>
        </Stack>
    );

    const renderEditForm = () => (
        <Card className="base-card">
            <Stack className="spacing-16px">
                <div className="text-bold text-size-large">
                    {editingForm.id === 0 ? "新規HRニュース追加" : "HRニュース編集"}
                </div>

                {/* バナー画像 */}
                <Stack className="spacing-8px">
                    <div className="input-label-text text-bold">バナー画像 / 動画</div>
                    <div className="text-size-small text-color-sub">
                        対応拡張子: {ACCEPTED_BANNER_EXTS.join(", ")}
                    </div>
                    <Row className="spacing-16px">
                        {/* 既存のバナーが登録済みで、かつ新規ファイル未選択のときだけ表示。
                            新規ファイル選択時は Uploader 側のプレビューに任せる。新規追加モード（id===0）では一切表示しない。 */}
                        {editingForm.id !== 0 && bannerFiles.length === 0 && editingForm.banner_preview_url && (
                            <div className="hr-news-banner-preview">
                                {isVideoPath(editingForm.banner_preview_url) ? (
                                    <video
                                        src={editingForm.banner_preview_url}
                                        className="hr-news-banner-preview-media"
                                        controls
                                        muted
                                        playsInline
                                        preload="metadata"
                                    />
                                ) : (
                                    <img
                                        src={editingForm.banner_preview_url}
                                        alt=""
                                        className="hr-news-banner-preview-media"
                                    />
                                )}
                            </div>
                        )}
                        <Uploader
                            className="custom-uploader"
                            accept={ACCEPTED_BANNER_EXTS}
                            onFileChanged={onBannerSelected}
                            files={bannerFiles}
                            isSwap={true}
                            minimum={false}
                        />
                    </Row>
                </Stack>

                {/* ありすのひとこと（任意） */}
                <Stack className="spacing-8px">
                    <div className="input-label-text text-bold">{Constants.AI_SHORT_NAME}のひとこと <span className="text-color-sub text-size-small">（任意）</span></div>
                    <TextInput
                        value={editingForm.card_text}
                        onchange={(e) => updateForm("card_text", e.target.value)}
                        placeholder={`（${Constants.AI_SHORT_NAME}のひとこと）に表示するテキスト ※未入力ならカードを非表示`}
                        rules={[{ type: "maxLength", value: 255 }]}
                    />
                </Stack>

                {/* 遷移先URL（任意） */}
                <Stack className="spacing-8px">
                    <div className="input-label-text text-bold">遷移先URL <span className="text-color-sub text-size-small">（任意）</span></div>
                    <TextInput
                        value={editingForm.url}
                        onchange={(e) => updateForm("url", e.target.value)}
                        placeholder="https://... ※未入力なら外部リンクなし"
                        rules={[{ type: "maxLength", value: 255 }]}
                    />
                </Stack>

                {/* AI参照用概要 */}
                {/*
                <Stack className="spacing-8px">
                    <div className="input-label-text text-bold">{Constants.AI_NAME}に渡す概要</div>
                    <FlexibleTextInput
                        multiline={true}
                        rows={4}
                        value={editingForm.description}
                        default={editingForm.description}
                        placeholder={`概要文（${Constants.AI_NAME}が応答生成時に参照します）`}
                        onchange={(e) => updateForm("description", e)}
                        maxHeight={200}
                    />
                </Stack>
                */}

                {/* 表示期間（秒指定可、ログ画面の CustomDateRangePicker と同じ inline + dialog 方式） */}
                <Row className="spacing-16px width-full">
                    <Stack className="spacing-8px width-full hr-news-datetime-cell">
                        <div className="input-label-text text-bold">表示開始日時</div>
                        {renderDateTimeTrigger("start")}
                    </Stack>
                    <Stack className="spacing-8px width-full hr-news-datetime-cell">
                        <div className="input-label-text text-bold">表示終了日時</div>
                        {renderDateTimeTrigger("end")}
                    </Stack>
                </Row>
                {openPicker && renderDateTimeDialog()}

                {/* 操作ボタン */}
                <Row className="hr-news-edit-actions flex-center spacing-16px">
                    <Button className="secondary-border width-160px" onClick={cancelEdit}>キャンセル</Button>
                    <Button
                        className={"width-160px " + (canSave() ? "primary" : "primary disabled")}
                        onClick={saveNews}
                        disabled={!canSave()}
                    >
                        保存
                    </Button>
                </Row>
            </Stack>
        </Card>
    );

    // エンドページ設定画面：機能ショートカットボタンの登録/削除
    const renderEndpageSettings = () => (
        <Card className="base-card">
            <Stack className="spacing-16px">
                <div className="text-bold text-size-large">エンドページ設定</div>
                <div className="text-size-small">
                    HRニュースの最終ページ（エンドページ）に表示する機能ショートカットボタンを設定します。
                </div>

                {endpageButtons.length === 0 ? (
                    <div className="hr-news-endpage-empty text-color-sub">
                        ボタンが登録されていません。「ボタン追加」から登録してください。
                    </div>
                ) : (
                    // 対話画面のエンドページ（横2個ずつのグリッド）に合わせて、設定欄も2列で並べる
                    <div className="hr-news-endpage-grid">
                        {endpageButtons.map((btn, index) => (
                            <Card key={index} className="hr-news-endpage-card">
                                <Stack className="spacing-12px width-full">
                                    <Row className="flex-both align-center width-full">
                                        <Row className="align-center spacing-8px">
                                            <span className="hr-news-endpage-badge">{index + 1}</span>
                                            <span className="text-bold text-size-normal"></span>
                                        </Row>
                                        <Button className="operate-button secondary-border danger hr-news-endpage-delete" onClick={() => removeEndpageButton(index)}>
                                            <Row className="text-size-normal text-normal-weight operate-label">削除</Row>
                                        </Button>
                                    </Row>
                                    <Stack className="spacing-4px">
                                        <div className="input-label-text text-bold">ラベル <span className="text-color-sub text-size-small">（20文字以内）</span></div>
                                        <TextInput
                                            value={btn.label}
                                            onchange={(e) => updateEndpageButton(index, "label", e.target.value)}
                                            placeholder=" "
                                            rules={[{ type: "maxLength", value: 20 }]}
                                        />
                                    </Stack>
                                    <Stack className="spacing-4px">
                                        <div className="input-label-text text-bold">{Constants.AI_NAME}の返答として表示する内容</div>
                                        <FlexibleTextInput
                                            multiline={true}
                                            rows={3}
                                            value={btn.message}
                                            default={btn.message}
                                            placeholder=" "
                                            onchange={(e) => updateEndpageButton(index, "message", e)}
                                            maxHeight={200}
                                        />
                                    </Stack>
                                    <Stack className="spacing-4px">
                                        <div className="input-label-text text-bold">
                                            チュートリアル矢印の対象ID <span className="text-color-sub text-size-small">（任意。対話画面側の要素IDを指定すると押下時に矢印で示します）</span>
                                        </div>
                                        <TextInput
                                            value={btn.target_id || ""}
                                            onchange={(e) => updateEndpageButton(index, "target_id", e.target.value)}
                                            placeholder="例: filter-button"
                                            rules={[{ type: "maxLength", value: 100 }]}
                                        />
                                    </Stack>
                                    {/* 矢印の表示位置（8方向）: target_id がある時だけ意味を持つ。中央セルは対象要素を表す。 */}
                                    { btn.target_id && (
                                        <Stack className="spacing-4px" style={{ opacity: (btn.target_id || "").trim() ? 1 : 0.5 }}>
                                            <div className="input-label-text text-bold">
                                                矢印の表示位置 <span className="text-color-sub text-size-small">（対象要素のどこに矢印を出すか）</span>
                                            </div>
                                            <div
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "repeat(3, 36px)",
                                                    gridTemplateRows: "repeat(3, 36px)",
                                                    gap: 4,
                                                }}
                                            >
                                                {[
                                                    ["tl", "↘"], ["t", "↓"], ["tr", "↙"],
                                                    ["l",  "→"], ["c",  ""], ["r",  "←"],
                                                    ["bl", "↗"], ["b", "↑"], ["br", "↖"],
                                                ].map(([key, glyph]) => {
                                                    if (key === "c") {
                                                        return (
                                                            <div
                                                                key="c"
                                                                style={{
                                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                                    fontSize: 10, color: "#888",
                                                                    border: "1px dashed #ccc", borderRadius: 4,
                                                                    userSelect: "none",
                                                                }}
                                                            >対象</div>
                                                        );
                                                    }
                                                    const selected = (btn.arrow_position || "t") === key;
                                                    return (
                                                        <button
                                                            key={key}
                                                            type="button"
                                                            onClick={() => updateEndpageButton(index, "arrow_position", key)}
                                                            style={{
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                fontSize: 20, lineHeight: 1,
                                                                background: selected ? "#5046E6" : "#fff",
                                                                color: selected ? "#fff" : "#444",
                                                                border: `1px solid ${selected ? "#5046E6" : "#bbb"}`,
                                                                borderRadius: 4, cursor: "pointer", padding: 0,
                                                            }}
                                                            aria-pressed={selected}
                                                            title={key}
                                                        >{glyph}</button>
                                                    );
                                                })}
                                            </div>
                                        </Stack>
                                     )}
                                </Stack>
                            </Card>
                        ))}
                    </div>
                )}

                <Row className="flex-center">
                    <Button
                        className={"primary-border width-240px " + (endpageButtons.length >= ENDPAGE_BUTTON_MAX ? "disabled" : "")}
                        onClick={addEndpageButton}
                        disabled={endpageButtons.length >= ENDPAGE_BUTTON_MAX}
                    >
                        <Row className="flex-center spacing-8px width-full">
                            <Icon className="label-icon" icon="plus" size={20} />
                            <div>ボタン追加（{endpageButtons.length}/{ENDPAGE_BUTTON_MAX}）</div>
                        </Row>
                    </Button>
                </Row>

                {/* 操作ボタン */}
                <Row className="hr-news-edit-actions flex-center spacing-16px">
                    <Button className="secondary-border width-160px" onClick={exitEditMode}>キャンセル</Button>
                    <Button
                        className={"width-160px " + (canSaveEndpage() ? "primary" : "primary disabled")}
                        onClick={saveEndpage}
                        disabled={!canSaveEndpage()}
                    >
                        保存
                    </Button>
                </Row>
            </Stack>
        </Card>
    );

    // 現在のサブ画面に応じて本文を切替
    const renderBody = () => {
        if (endpageMode) return renderEndpageSettings();
        if (editingForm !== null) return renderEditForm();
        return renderList();
    };

    return (
        <PageContent pageId="AdminHrNewsEdit">
            <PageHeader>
                <Row className="flex-both width-full align-center">
                    <Stack className="spacing-4px">
                        <div className="page-header-title">HRニュース管理</div>
                        <div className="page-header-detail">HRニュースの登録・編集・削除が行えます。</div>
                    </Stack>
                    {editingForm === null && !endpageMode && (
                        <Row className="flex-right spacing-8px">
                            <Button className="secondary-border hr-news-header-button" onClick={startEndpageSettings}>
                                <Row className="flex-center spacing-8px">
                                    <Icon className="label-icon" icon="gear" size={20} />
                                    <div>エンドページ設定</div>
                                </Row>
                            </Button>
                            <Button className="primary hr-news-header-button" onClick={startCreate}>
                                <Row className="flex-center spacing-8px">
                                    <Icon className="label-icon" icon="plus" size={20} />
                                    <div>新規追加</div>
                                </Row>
                            </Button>
                        </Row>
                    )}
                    {isInSubScreen && (
                        <Row className="flex-right">
                            <Button className="secondary-border hr-news-header-button" onClick={exitEditMode}>
                                <Row className="flex-center spacing-8px">
                                    <Icon className="label-icon" icon="chevron-left" size={20} />
                                    <div>戻る</div>
                                </Row>
                            </Button>
                        </Row>
                    )}
                </Row>
            </PageHeader>
            <PageWrapper>
                <PageContainer>
                    <Stack className="block-center">
                        {renderBody()}
                    </Stack>
                </PageContainer>
            </PageWrapper>
            <CommonModal modalInfo={modalInfo} setModalInfo={setModalInfo} />
        </PageContent>
    );
};

export default AdminHrNewsEdit;
